import React, {
  ChangeEvent,
  FC,
  InputHTMLAttributes,
  useId,
  useState,
} from 'react';

import Chip from '../Chip/Chip';
import { twMerge } from 'tailwind-merge';

interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  showButton?: boolean;
  tags?: string[];
  onTagChange?: (v: string) => void;
}

const TagsField: FC<IProps> = ({
  value,
  label,
  tags = [],
  onTagChange,
  ...rest
}) => {
  const id = useId();

  const [val, setVal] = useState('');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setVal(e.currentTarget.value);
  };

  const addTags = (input: string) => {
    const MAX_TAG_LENGTH = 30;
    const MAX_TAGS = 5;
    
    // Don't add any tags if we're already at the limit
    if (tags.length >= MAX_TAGS) {
      return;
    }
    
    const newTags = input
      .split(',')
      .map(tag => tag.trim())
      .filter(
        tag =>
          tag.length > 0 &&
          tag.length <= MAX_TAG_LENGTH &&
          !tags.includes(tag)
      ); // Check for duplicates

    // Only add tags up to the limit
    const remainingSlots = MAX_TAGS - tags.length;
    const tagsToAdd = newTags.slice(0, remainingSlots);

    tagsToAdd.forEach(tag => {
      if (typeof onTagChange === 'function') {
        onTagChange(tag);
      }
    });
  };

  return (
    <div className='w-full max-w-[488px]  min-h-[65px] border border-k-input-border flex flex-col p-3 rounded-[12px] relative group focus-within:border-k-black focus-within:shadow-border'>
      <div className='flex items-center gap-[5px] flex-wrap mt-[15px]'>
        {tags?.map(tag => (
          <Chip
            key={tag}
            label={tag}
            onDelete={() => {
              onTagChange && onTagChange(tag);
            }}
          />
        ))}
        <input
          className='flex-1 peer border border-k-black outline-none border-none text-lg font-medium placeholder:text-transparent min-w-[100px] '
          placeholder=''
          value={val}
          onChange={handleChange}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (val.trim()) {
                addTags(val);
                setVal('');
              }
            } else if (e.key === 'Backspace' && val === '' && tags.length > 0) {
              e.preventDefault();
              // Remove the last tag when backspace is pressed on empty input
              const lastTag = tags[tags.length - 1];
              if (typeof onTagChange === 'function') {
                onTagChange(lastTag);
              }
            }
          }}
          onBlur={() => {
            if (val.trim()) {
              addTags(val);
              setVal('');
            }
          }}
          {...rest}
          id={id}
        />
      </div>
      <p
        className={twMerge(
          'text-lg text-k-grey-text absolute top-[17px]  peer-[:focus]:text-sm peer-[:focus]:top-[6px] peer-[:not(:placeholder-shown)]:text-sm  peer-[:not(:placeholder-shown)]:top-[6px] transition-[top] duration-200 ease pointer-events-none',
          (val || tags?.length) && 'top-2 text-sm'
        )}
      >
        {label}
      </p>
    </div>
  );
};

export default TagsField;
